import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Printer, 
  FileText, 
  Users, 
  Truck, 
  DollarSign,
  Download,
  RefreshCw,
  Calendar,
  Building2,
  Receipt
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return `R ${(parseFloat(value) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" }
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

// Company Header with Logo Component (used in all printable reports)
const CompanyHeader = ({ companySettings, title }) => {
  if (!companySettings) return null;
  
  return (
    <div className="flex items-start justify-between border-b-2 border-black pb-4 mb-6">
      {/* Logo on left */}
      <div className="flex-shrink-0">
        {companySettings.logo_url ? (
          <img 
            src={companySettings.logo_url} 
            alt="Company Logo" 
            style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain' }}
          />
        ) : (
          <div style={{ width: '120px', height: '60px', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
            <span style={{ color: '#9ca3af', fontSize: '10px' }}>NO LOGO</span>
          </div>
        )}
      </div>
      
      {/* Company Info on right */}
      <div className="text-right">
        <h1 className="text-xl font-bold">{companySettings.company_name || 'Company Name'}</h1>
        {companySettings.phone && <p className="text-sm text-gray-600">{companySettings.phone}</p>}
        {companySettings.email && <p className="text-sm text-gray-600">{companySettings.email}</p>}
        {companySettings.vat_number && <p className="text-sm text-gray-600">VAT: {companySettings.vat_number}</p>}
      </div>
    </div>
  );
};

// Printable Monthly Summary Component
const MonthlySummaryPrint = ({ data, companySettings }) => {
  if (!data) return null;
  
  return (
    <div className="print-content p-8 bg-white" style={{ fontFamily: "Arial, sans-serif" }}>
      {/* Header with Logo */}
      <CompanyHeader companySettings={companySettings} title="MONTHLY FINANCIAL SUMMARY" />
      
      {/* Report Title */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold">MONTHLY FINANCIAL SUMMARY</h1>
        <h2 className="text-lg">{data.period?.month_name} {data.period?.year}</h2>
        <p className="text-sm text-gray-600">Period: {data.period?.start_date} to {data.period?.end_date}</p>
      </div>

      {/* Revenue Section */}
      <div className="mb-6">
        <h3 className="font-bold text-lg border-b mb-2">REVENUE</h3>
        <table className="w-full text-sm">
          <tbody>
            <tr><td>Gross Sales (Incl VAT)</td><td className="text-right">{formatCurrency(data.revenue?.gross_sales_incl_vat)}</td></tr>
            <tr><td>Less: Refunds</td><td className="text-right text-red-600">({formatCurrency(data.revenue?.refunds)})</td></tr>
            <tr className="border-t font-semibold"><td>Net Sales (Incl VAT)</td><td className="text-right">{formatCurrency(data.revenue?.net_sales_incl_vat)}</td></tr>
            <tr><td>VAT on Sales (15%)</td><td className="text-right">({formatCurrency(data.revenue?.vat_on_sales)})</td></tr>
            <tr className="border-t font-bold bg-gray-100"><td>Net Sales (Excl VAT)</td><td className="text-right">{formatCurrency(data.revenue?.net_sales_excl_vat)}</td></tr>
          </tbody>
        </table>
      </div>

      {/* Payment Methods */}
      <div className="mb-6">
        <h3 className="font-bold text-lg border-b mb-2">PAYMENT BREAKDOWN</h3>
        <table className="w-full text-sm">
          <tbody>
            <tr><td>Cash</td><td className="text-right">{formatCurrency(data.payment_breakdown?.cash)}</td></tr>
            <tr><td>Card</td><td className="text-right">{formatCurrency(data.payment_breakdown?.card)}</td></tr>
            <tr><td>Account</td><td className="text-right">{formatCurrency(data.payment_breakdown?.account)}</td></tr>
            <tr><td>EFT</td><td className="text-right">{formatCurrency(data.payment_breakdown?.eft)}</td></tr>
            <tr className="border-t font-semibold"><td>Total</td><td className="text-right">{formatCurrency(data.payment_breakdown?.total)}</td></tr>
          </tbody>
        </table>
      </div>

      {/* Cost of Sales & Profit */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="font-bold text-lg border-b mb-2">COST OF SALES</h3>
          <table className="w-full text-sm">
            <tbody>
              <tr><td>Cost of Goods Sold</td><td className="text-right">{formatCurrency(data.cost_of_sales?.cost_of_goods_sold)}</td></tr>
              <tr><td>Purchases (Incl VAT)</td><td className="text-right">{formatCurrency(data.cost_of_sales?.total_purchases_incl_vat)}</td></tr>
            </tbody>
          </table>
          {data.data_source && (
            <p className="text-xs text-gray-500 mt-1">
              Data: {data.data_source.primary === 'imported' ? 'Imported Sales' : 'Live POS'}
            </p>
          )}
        </div>
        <div>
          <h3 className="font-bold text-lg border-b mb-2">PROFIT</h3>
          <table className="w-full text-sm">
            <tbody>
              <tr className="text-green-700"><td>Gross Profit (products)</td><td className="text-right">{formatCurrency(data.profit?.gross_profit)} ({data.profit?.gross_profit_percent}%)</td></tr>
              {(data.service_revenue?.total_revenue || 0) > 0 && (
                <>
                  <tr className="text-xs text-gray-600"><td className="pl-3">+ Service Revenue (airtime/electricity/loans)</td><td className="text-right">{formatCurrency(data.service_revenue?.total_revenue)}</td></tr>
                  <tr className="text-xs text-gray-600"><td className="pl-3">− Service Cost</td><td className="text-right">({formatCurrency(data.service_revenue?.total_cost)})</td></tr>
                  <tr className="text-xs text-green-700"><td className="pl-3">= Service Margin</td><td className="text-right">{formatCurrency(data.service_revenue?.service_profit)}</td></tr>
                </>
              )}
              <tr><td>Less: Expenses</td><td className="text-right text-red-600">({formatCurrency(data.expenses?.total)})</td></tr>
              {(data.expenses?.informational_total || 0) > 0 && (
                <tr className="text-xs text-gray-500">
                  <td className="pl-3">Stock Purchases (informational only — not deducted)</td>
                  <td className="text-right">{formatCurrency(data.expenses?.informational_total)}</td>
                </tr>
              )}
              <tr className={`border-t font-bold ${data.profit?.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                <td>Net Profit</td><td className="text-right">{formatCurrency(data.profit?.net_profit)} ({data.profit?.net_profit_percent}%)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* VAT Summary */}
      <div className="mb-6 p-4 border-2 border-black">
        <h3 className="font-bold text-lg mb-2">VAT SUMMARY FOR SARS</h3>
        <table className="w-full text-sm">
          <tbody>
            <tr><td>Output VAT (Sales)</td><td className="text-right">{formatCurrency(data.vat?.output_vat)}</td></tr>
            <tr><td>Input VAT (Purchases)</td><td className="text-right">({formatCurrency(data.vat?.input_vat)})</td></tr>
            <tr className={`border-t-2 font-bold text-lg ${data.vat?.vat_payable >= 0 ? 'text-red-700' : 'text-green-700'}`}>
              <td>{data.vat?.status}</td>
              <td className="text-right">{formatCurrency(Math.abs(data.vat?.vat_payable))}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4 text-center text-sm border-t pt-4">
        <div><div className="font-bold text-xl">{data.statistics?.transaction_count}</div><div>Transactions</div></div>
        <div><div className="font-bold text-xl">{data.statistics?.refund_count}</div><div>Refunds</div></div>
        <div><div className="font-bold text-xl">{data.statistics?.grv_count}</div><div>GRVs</div></div>
        <div><div className="font-bold text-xl">{formatCurrency(data.statistics?.average_sale)}</div><div>Avg Sale</div></div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t text-center text-xs text-gray-500">
        <p>Generated: {new Date().toLocaleString()}</p>
        <p>This report is for internal use only. Please verify all figures before external submission.</p>
      </div>
    </div>
  );
};

// Printable Supplier Balances Component
const SupplierBalancesPrint = ({ data, companySettings }) => {
  if (!data) return null;
  
  return (
    <div className="print-content p-8 bg-white" style={{ fontFamily: "Arial, sans-serif" }}>
      <CompanyHeader companySettings={companySettings} />
      
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold">SUPPLIER BALANCES</h1>
        <h2 className="text-lg">Accounts Payable Report</h2>
        <p className="text-sm text-gray-600">As at: {data.as_at_date}</p>
        {data.snapshot?.used ? (
          <p className="text-xs text-blue-700" data-testid="audit-pack-supplier-snapshot-used-label">
            Source: Locked month-end snapshot ({data.snapshot.snapshot_id})
          </p>
        ) : null}
        {data.exclude_consignment ? (
          <p className="text-xs text-gray-500" data-testid="audit-pack-exclude-consignment-label">Consignment suppliers excluded</p>
        ) : null}
      </div>

      <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 text-center">
        <div className="text-sm">Total Owed to Suppliers</div>
        <div className="text-3xl font-bold text-red-700">{formatCurrency(data.total_owed_to_suppliers)}</div>
        <div className="text-sm text-gray-600">{data.supplier_count} suppliers with outstanding balance</div>
        {data.total_payable_this_month !== undefined && (
          <div className="mt-2 pt-2 border-t border-red-200">
            <div className="text-sm text-orange-700">Payable This Month: <span className="font-bold">{formatCurrency(data.total_payable_this_month)}</span></div>
            {data.total_not_yet_due > 0 && (
              <div className="text-xs text-gray-500">Not Yet Due: {formatCurrency(data.total_not_yet_due)}</div>
            )}
          </div>
        )}
        {data.merged_duplicate_rows > 0 ? (
          <div className="text-xs text-gray-500 mt-1" data-testid="audit-pack-supplier-duplicates-merged-note">
            Duplicate supplier names merged: {data.merged_duplicate_rows}
          </div>
        ) : null}
      </div>

      {data.comparison?.before_total_raw !== undefined && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 text-sm" data-testid="audit-pack-supplier-before-after">
          <div><strong>Before (raw):</strong> {formatCurrency(data.comparison.before_total_raw)}</div>
          <div><strong>After (corrected):</strong> {formatCurrency(data.comparison.after_total_corrected)}</div>
          <div><strong>Difference:</strong> {formatCurrency(data.comparison.difference)}</div>
        </div>
      )}

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-200">
            <th className="border p-2 text-left">Supplier Name</th>
            <th className="border p-2 text-center">Type</th>
            <th className="border p-2 text-right">Not Yet Due</th>
            <th className="border p-2 text-right">Payable This Month</th>
            <th className="border p-2 text-right">Overdue (30/60/90+)</th>
            <th className="border p-2 text-right">Total Balance</th>
          </tr>
        </thead>
        <tbody>
          {data.suppliers?.map((supplier, idx) => {
            const overdue = (supplier.days_30 || 0) + (supplier.days_60 || 0) + (supplier.days_90 || 0) + (supplier.days_120 || 0) + (supplier.over_120 || 0);
            return (
            <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${supplier.is_consignment ? 'bg-purple-50' : ''}`} data-testid={`supplier-row-${supplier.supplier_id}`}>
              <td className="border p-2 font-medium">{supplier.supplier_name}</td>
              <td className="border p-2 text-center">
                {supplier.is_consignment ? (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">CONSIGNMENT</span>
                ) : supplier.uses_statement_aging ? (
                  <span className="text-blue-600 text-xs">30 Days</span>
                ) : (
                  <span className="text-gray-400 text-xs">Standard</span>
                )}
              </td>
              <td className="border p-2 text-right text-gray-600">{formatCurrency(supplier.not_yet_due || 0)}</td>
              <td className="border p-2 text-right text-orange-700 font-semibold">{formatCurrency(supplier.payable_this_month || 0)}</td>
              <td className={`border p-2 text-right ${overdue > 0 ? 'text-red-700 font-semibold' : 'text-gray-400'}`}>{formatCurrency(overdue)}</td>
              <td className="border p-2 text-right font-semibold">{formatCurrency(supplier.balance)}</td>
            </tr>
          );})}
        </tbody>
        <tfoot>
          <tr className="bg-gray-300 font-bold">
            <td className="border p-2" colSpan={2}>TOTAL</td>
            <td className="border p-2 text-right text-gray-700">{formatCurrency(data.total_not_yet_due || 0)}</td>
            <td className="border p-2 text-right text-orange-700">{formatCurrency(data.total_payable_this_month || 0)}</td>
            <td className="border p-2 text-right text-red-700">{formatCurrency((data.total_owed_to_suppliers || 0) - (data.total_payable_this_month || 0) - (data.total_not_yet_due || 0))}</td>
            <td className="border p-2 text-right">{formatCurrency(data.total_owed_to_suppliers)}</td>
          </tr>
        </tfoot>
      </table>
      
      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-semibold">CONSIGNMENT</span>
          <span className="text-gray-600">= Stock owned by supplier until sold</span>
        </div>
      </div>

      <div className="mt-8 pt-4 border-t text-center text-xs text-gray-500">
        <p>Generated: {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
};

// Printable Customer Balances Component
const CustomerBalancesPrint = ({ data, companySettings, debtorsOnly = false }) => {
  if (!data) return null;

  // Optionally filter to only customers that owe money (balance > 0)
  const allCustomers = data.customers || [];
  const filteredCustomers = debtorsOnly
    ? allCustomers.filter((c) => (parseFloat(c.balance) || 0) > 0)
    : allCustomers;
  const filteredTotal = filteredCustomers.reduce(
    (sum, c) => sum + (parseFloat(c.balance) || 0),
    0
  );

  return (
    <div className="print-content p-8 bg-white" style={{ fontFamily: "Arial, sans-serif" }}>
      <CompanyHeader companySettings={companySettings} />
      
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold">
          {debtorsOnly ? "DEBTORS LIST (CUSTOMERS WHO OWE)" : "CUSTOMER BALANCES"}
        </h1>
        <h2 className="text-lg">Accounts Receivable Report</h2>
        <p className="text-sm text-gray-600">As at: {data.as_at_date}</p>
        {data.snapshot?.used ? (
          <p className="text-xs text-blue-700" data-testid="audit-pack-customer-snapshot-used-label">
            Source: Locked month-end snapshot ({data.snapshot.snapshot_id})
          </p>
        ) : (
          <p className="text-xs text-green-700 font-semibold" data-testid="audit-pack-customer-live-label">
            ✓ LIVE DATA - Current balances from customer accounts
          </p>
        )}
      </div>

      <div className="mb-6 p-4 bg-green-50 border-2 border-green-300 text-center">
        <div className="text-sm">{debtorsOnly ? "Total Debtor Balance (Filtered)" : "Total Debtor Balance"}</div>
        <div className="text-3xl font-bold text-green-700">
          {formatCurrency(debtorsOnly ? filteredTotal : data.total_owed_by_customers)}
        </div>
        {!debtorsOnly && (
          <>
            <div className="text-sm text-red-700">Total Credit Balance: {formatCurrency(data.total_credit_balances || 0)}</div>
            <div className="text-sm text-gray-700">Net Customer Balance: {formatCurrency(data.net_customer_balance || 0)}</div>
          </>
        )}
        <div className="text-sm text-gray-600">
          {debtorsOnly
            ? `${filteredCustomers.length} customers who owe money (credits excluded)`
            : `${data.customer_count} customers with non-zero balance`}
        </div>
      </div>

      {data.comparison && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 text-sm" data-testid="audit-pack-customer-before-after">
          <div><strong>Before (raw):</strong> {formatCurrency(data.comparison.before_total_raw)}</div>
          <div><strong>After (corrected):</strong> {formatCurrency(data.comparison.after_total_corrected)}</div>
          <div><strong>Difference:</strong> {formatCurrency(data.comparison.difference)}</div>
        </div>
      )}

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-200">
            <th className="border p-2 text-left">Account #</th>
            <th className="border p-2 text-left">Customer Name</th>
            <th className="border p-2 text-right">Current</th>
            <th className="border p-2 text-right">30 Days</th>
            <th className="border p-2 text-right">60 Days</th>
            <th className="border p-2 text-right">90+ Days</th>
            <th className="border p-2 text-right">Balance</th>
          </tr>
        </thead>
        <tbody>
          {filteredCustomers.map((customer, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="border p-2">{customer.account_number}</td>
              <td className="border p-2 font-medium">{customer.customer_name}</td>
              <td className="border p-2 text-right">{formatCurrency(customer.aging_current)}</td>
              <td className="border p-2 text-right">{formatCurrency(customer.aging_30)}</td>
              <td className="border p-2 text-right">{formatCurrency(customer.aging_60)}</td>
              <td className="border p-2 text-right">{formatCurrency((customer.aging_90 || 0) + (customer.aging_120 || 0))}</td>
              <td className="border p-2 text-right font-semibold">{formatCurrency(customer.balance)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-gray-300 font-bold">
            <td className="border p-2" colSpan={6}>{debtorsOnly ? "TOTAL OWED BY DEBTORS" : "TOTAL DEBTOR BALANCE"}</td>
            <td className="border p-2 text-right">{formatCurrency(debtorsOnly ? filteredTotal : data.total_owed_by_customers)}</td>
          </tr>
          {!debtorsOnly && (
            <>
              <tr className="bg-gray-200 font-semibold">
                <td className="border p-2" colSpan={6}>TOTAL CREDIT BALANCE</td>
                <td className="border p-2 text-right">{formatCurrency(data.total_credit_balances || 0)}</td>
              </tr>
              <tr className="bg-gray-100 font-semibold">
                <td className="border p-2" colSpan={6}>NET CUSTOMER BALANCE</td>
                <td className="border p-2 text-right">{formatCurrency(data.net_customer_balance || 0)}</td>
              </tr>
            </>
          )}
        </tfoot>
      </table>

      <div className="mt-8 pt-4 border-t text-center text-xs text-gray-500">
        <p>Generated: {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
};

// Printable VAT Report Component
const VATReportPrint = ({ data, companySettings }) => {
  if (!data) return null;
  
  return (
    <div className="print-content p-8 bg-white" style={{ fontFamily: "Arial, sans-serif" }}>
      <CompanyHeader companySettings={companySettings} />
      
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold">VAT REPORT FOR SARS</h1>
        <h2 className="text-lg">{data.vat_period?.month_name} {data.vat_period?.year}</h2>
        <p className="text-sm text-gray-600">Period: {data.vat_period?.start_date} to {data.vat_period?.end_date}</p>
        <p className="text-sm">VAT Rate: {data.vat_period?.vat_rate}</p>
      </div>

      {/* Output VAT */}
      <div className="mb-6">
        <h3 className="font-bold text-lg border-b-2 border-green-600 text-green-700 mb-3">OUTPUT VAT (Sales)</h3>
        <p className="text-xs text-gray-600 mb-2">{data.output_vat?.description}</p>
        <table className="w-full text-sm mb-4">
          <tbody>
            <tr><td>Total Sales (Incl VAT)</td><td className="text-right">{formatCurrency(data.output_vat?.total_sales_incl_vat)}</td></tr>
            <tr><td>Less: Refunds</td><td className="text-right text-red-600">({formatCurrency(data.output_vat?.less_refunds)})</td></tr>
            <tr className="border-t font-semibold"><td>Net Sales (Incl VAT)</td><td className="text-right">{formatCurrency(data.output_vat?.net_sales_incl_vat)}</td></tr>
          </tbody>
        </table>
        
        <div className="bg-gray-100 p-3 mb-4">
          <div className="font-semibold mb-2">Sales Breakdown:</div>
          <table className="w-full text-sm">
            <tbody>
              <tr><td>Standard Rated (15%)</td><td className="text-right">{formatCurrency(data.output_vat?.breakdown?.["standard_rated_15%"])}</td></tr>
              <tr><td>Zero Rated (0%)</td><td className="text-right">{formatCurrency(data.output_vat?.breakdown?.["zero_rated_0%"])}</td></tr>
              <tr><td>Exempt</td><td className="text-right">{formatCurrency(data.output_vat?.breakdown?.exempt)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="bg-green-100 p-3 border border-green-400">
          <div className="flex justify-between font-bold">
            <span>VAT ON SALES (OUTPUT VAT)</span>
            <span>{formatCurrency(data.output_vat?.vat_on_sales)}</span>
          </div>
          <div className="text-xs text-gray-600">{data.output_vat?.transaction_count} transactions</div>
        </div>
      </div>

      {/* Input VAT */}
      <div className="mb-6">
        <h3 className="font-bold text-lg border-b-2 border-blue-600 text-blue-700 mb-3">INPUT VAT (Purchases & Expenses)</h3>
        <p className="text-xs text-gray-600 mb-2">{data.input_vat?.description}</p>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-100 p-3">
            <div className="font-semibold mb-2">Purchases (Stock)</div>
            <table className="w-full text-sm">
              <tbody>
                <tr><td>Total (Incl VAT)</td><td className="text-right">{formatCurrency(data.input_vat?.purchases?.total_incl_vat)}</td></tr>
                <tr className="font-semibold"><td>VAT Claimed</td><td className="text-right">{formatCurrency(data.input_vat?.purchases?.vat_claimed)}</td></tr>
              </tbody>
            </table>
            <div className="text-xs text-gray-600 mt-1">{data.input_vat?.purchases?.grv_count} GRVs</div>
          </div>
          <div className="bg-gray-100 p-3">
            <div className="font-semibold mb-2">Expenses</div>
            <table className="w-full text-sm">
              <tbody>
                <tr><td>Total (Incl VAT)</td><td className="text-right">{formatCurrency(data.input_vat?.expenses?.total_incl_vat)}</td></tr>
                <tr className="font-semibold"><td>VAT Claimed</td><td className="text-right">{formatCurrency(data.input_vat?.expenses?.vat_claimed)}</td></tr>
              </tbody>
            </table>
            <div className="text-xs text-gray-600 mt-1">{data.input_vat?.expenses?.expense_count} expenses</div>
          </div>
        </div>

        <div className="bg-blue-100 p-3 border border-blue-400">
          <div className="flex justify-between font-bold">
            <span>TOTAL INPUT VAT</span>
            <span>{formatCurrency(data.input_vat?.total_input_vat)}</span>
          </div>
        </div>
      </div>

      {/* VAT Calculation */}
      <div className="border-4 border-black p-6 mb-6">
        <h3 className="font-bold text-xl mb-4 text-center">VAT CALCULATION</h3>
        <table className="w-full text-lg">
          <tbody>
            <tr>
              <td className="py-2">Output VAT (Sales)</td>
              <td className="text-right py-2">{formatCurrency(data.vat_calculation?.output_vat)}</td>
            </tr>
            <tr>
              <td className="py-2">Less: Input VAT (Purchases & Expenses)</td>
              <td className="text-right py-2">({formatCurrency(data.vat_calculation?.less_input_vat)})</td>
            </tr>
            <tr className={`border-t-4 border-black font-bold text-xl ${data.vat_calculation?.vat_payable >= 0 ? 'text-red-700' : 'text-green-700'}`}>
              <td className="py-3">{data.vat_calculation?.status}</td>
              <td className="text-right py-3">{formatCurrency(Math.abs(data.vat_calculation?.vat_payable))}</td>
            </tr>
          </tbody>
        </table>
        <div className="text-center text-sm mt-3 text-gray-600">Due Date: {data.vat_calculation?.due_date}</div>
      </div>

      {/* Declaration */}
      <div className="border-t-2 pt-4">
        <div className="grid grid-cols-2 gap-8 mt-8">
          <div>
            <div className="border-b border-black h-8 mb-1"></div>
            <div className="text-sm">Prepared By</div>
          </div>
          <div>
            <div className="border-b border-black h-8 mb-1"></div>
            <div className="text-sm">Approved By</div>
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-4">
          <p>Prepared: {data.declaration?.prepared_date}</p>
          <p>{data.declaration?.note}</p>
        </div>
      </div>
    </div>
  );
};

export default function AuditPack() {
  const [activeTab, setActiveTab] = useState("monthly");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [excludeConsignmentSuppliers, setExcludeConsignmentSuppliers] = useState(false);
  const [useSnapshotMode, setUseSnapshotMode] = useState(false);
  const [snapshotStatus, setSnapshotStatus] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotGenerating, setSnapshotGenerating] = useState(false);
  const [downloadingCustomerDebug, setDownloadingCustomerDebug] = useState(false);
  const [debtorsOnly, setDebtorsOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [monthlyData, setMonthlyData] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [vatData, setVatData] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);

  const printRef = useRef(null);
  
  // Fetch company settings for logo on mount
  useEffect(() => {
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    fetchSnapshotStatus();
  }, [selectedYear, selectedMonth]);
  
  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/company`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error("Error fetching company settings:", error);
    }
  };

  const fetchSnapshotStatus = async () => {
    setSnapshotLoading(true);
    try {
      const response = await axios.get(`${API}/audit-pack/month-end-snapshot/status`, {
        params: { year: selectedYear, month: selectedMonth },
      });
      setSnapshotStatus(response.data);
    } catch (error) {
      console.error("Error fetching snapshot status:", error);
      setSnapshotStatus(null);
    } finally {
      setSnapshotLoading(false);
    }
  };

  const handleGenerateSnapshot = async () => {
    setSnapshotGenerating(true);
    try {
      const response = await axios.post(`${API}/audit-pack/month-end-snapshot/generate`, null, {
        params: {
          year: selectedYear,
          month: selectedMonth,
          force_refresh: true,
          lock_snapshot: true,
        }
      });
      toast.success(response.data?.message || "Month-end snapshot generated");
      await fetchSnapshotStatus();
    } catch (error) {
      console.error("Error generating month-end snapshot:", error);
      toast.error(error.response?.data?.detail || "Failed to generate month-end snapshot");
    } finally {
      setSnapshotGenerating(false);
    }
  };

  const fetchMonthlySummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/audit-pack/monthly-summary`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setMonthlyData(response.data);
      toast.success("Monthly summary loaded");
    } catch (error) {
      console.error("Error fetching monthly summary:", error);
      toast.error("Failed to fetch monthly summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplierBalances = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/audit-pack/supplier-balances`, {
        params: {
          // Pass the selected period so the backend applies a cutoff and
          // returns balances AS AT the last day of that month (rather
          // than today). When ``use_snapshot`` is on we pull from the
          // locked archive; otherwise we compute the same numbers live
          // against the current ledger with the cutoff filter.
          year: selectedYear,
          month: selectedMonth,
          create_backup: false,
          exclude_consignment: excludeConsignmentSuppliers,
          use_snapshot: useSnapshotMode,
        }
      });
      setSupplierData(response.data);
      toast.success(useSnapshotMode ? "Snapshot supplier balances loaded" : `Supplier balances as at ${response.data?.as_at_date || "period end"}`);
    } catch (error) {
      console.error("Error fetching supplier balances:", error);
      toast.error("Failed to fetch supplier balances");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerBalances = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/audit-pack/customer-balances`, {
        params: {
          year: selectedYear,
          month: selectedMonth,
          create_backup: false,
          use_snapshot: useSnapshotMode,
          allow_name_match: false,
          include_credit_balances: true,
          include_zero_balances: false,
        }
      });
      setCustomerData(response.data);
      toast.success(useSnapshotMode ? "Snapshot customer balances loaded" : `Customer balances as at ${response.data?.as_at_date || "period end"}`);
    } catch (error) {
      console.error("Error fetching customer balances:", error);
      toast.error("Failed to fetch customer balances");
    } finally {
      setLoading(false);
    }
  };

  const downloadCustomerDebugJSON = async () => {
    setDownloadingCustomerDebug(true);
    try {
      const response = await axios.get(`${API}/audit-pack/customer-balances`, {
        params: {
          year: selectedYear,
          month: selectedMonth,
          create_backup: false,
          use_snapshot: useSnapshotMode,
          allow_name_match: false,
          include_debug_export: true,
          include_credit_balances: true,
          include_zero_balances: false,
        }
      });

      const payload = response.data;
      const dataStr = JSON.stringify(payload, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-customer-debug-${selectedYear}-${String(selectedMonth).padStart(2, "0")}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Customer debug JSON downloaded");
    } catch (error) {
      console.error("Error downloading customer debug JSON:", error);
      toast.error(error.response?.data?.detail || "Failed to download customer debug JSON");
    } finally {
      setDownloadingCustomerDebug(false);
    }
  };

  const fetchVATReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/audit-pack/vat-report`, {
        params: { year: selectedYear, month: selectedMonth }
      });
      setVatData(response.data);
      toast.success("VAT report loaded");
    } catch (error) {
      console.error("Error fetching VAT report:", error);
      toast.error("Failed to fetch VAT report");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Audit Report</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .text-right { text-align: right; }
            .text-center { text-center; }
            .font-bold { font-weight: bold; }
            .font-semibold { font-weight: 600; }
            .text-red-600, .text-red-700 { color: #dc2626; }
            .text-green-600, .text-green-700 { color: #16a34a; }
            .text-blue-700 { color: #1d4ed8; }
            .text-gray-500, .text-gray-600 { color: #6b7280; }
            .bg-gray-50 { background-color: #f9fafb; }
            .bg-gray-100 { background-color: #f3f4f6; }
            .bg-gray-200 { background-color: #e5e7eb; }
            .bg-gray-300 { background-color: #d1d5db; }
            .bg-red-50 { background-color: #fef2f2; }
            .bg-green-50 { background-color: #f0fdf4; }
            .bg-green-100 { background-color: #dcfce7; }
            .bg-blue-100 { background-color: #dbeafe; }
            .border-red-300 { border-color: #fca5a5; }
            .border-green-300, .border-green-400 { border-color: #86efac; }
            .border-blue-400 { border-color: #60a5fa; }
            .border-green-600 { border-color: #16a34a; }
            .border-blue-600 { border-color: #2563eb; }
            .mb-2 { margin-bottom: 0.5rem; }
            .mb-3 { margin-bottom: 0.75rem; }
            .mb-4 { margin-bottom: 1rem; }
            .mb-6 { margin-bottom: 1.5rem; }
            .mt-1 { margin-top: 0.25rem; }
            .mt-2 { margin-top: 0.5rem; }
            .mt-3 { margin-top: 0.75rem; }
            .mt-4 { margin-top: 1rem; }
            .mt-8 { margin-top: 2rem; }
            .p-2 { padding: 0.5rem; }
            .p-3 { padding: 0.75rem; }
            .p-4 { padding: 1rem; }
            .p-6 { padding: 1.5rem; }
            .pt-4 { padding-top: 1rem; }
            .pb-4 { padding-bottom: 1rem; }
            .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
            .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
            .border { border: 1px solid #e5e7eb; }
            .border-2 { border: 2px solid #e5e7eb; }
            .border-4 { border: 4px solid #000; }
            .border-t { border-top: 1px solid #e5e7eb; }
            .border-t-2 { border-top: 2px solid #e5e7eb; }
            .border-t-4 { border-top: 4px solid #000; }
            .border-b { border-bottom: 1px solid #e5e7eb; }
            .border-b-2 { border-bottom: 2px solid #000; }
            .border-black { border-color: #000; }
            .grid { display: grid; }
            .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
            .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
            .gap-4 { gap: 1rem; }
            .gap-6 { gap: 1.5rem; }
            .gap-8 { gap: 2rem; }
            .text-sm { font-size: 0.875rem; }
            .text-xs { font-size: 0.75rem; }
            .text-lg { font-size: 1.125rem; }
            .text-xl { font-size: 1.25rem; }
            .text-2xl { font-size: 1.5rem; }
            .text-3xl { font-size: 1.875rem; }
            .h-8 { height: 2rem; }
            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-pack-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Pack</h1>
          <p className="text-gray-500">Monthly reports for your auditors</p>
        </div>
      </div>

      {/* Period Selector */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-500" />
              <span className="font-medium">Select Period:</span>
            </div>
            <div className="flex gap-2">
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-40" data-testid="month-select">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-28" data-testid="year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 p-3 border rounded bg-blue-50" data-testid="audit-pack-snapshot-control-panel">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="text-sm">
                <div className="font-semibold text-blue-900">Month-end Balances</div>
                <div className="text-blue-700" data-testid="audit-pack-snapshot-status-text">
                  {useSnapshotMode
                    ? (snapshotLoading
                        ? "Checking snapshot..."
                        : snapshotStatus?.exists
                          ? `Reading LOCKED snapshot • as at ${snapshotStatus.as_at_date || "N/A"}${snapshotStatus.locked ? " (locked)" : ""}`
                          : "No snapshot yet — click Generate below")
                    : `Computing LIVE balances as at last day of ${new Date(selectedYear, selectedMonth - 1).toLocaleString("en", { month: "long" })} ${selectedYear}. Toggle Snapshot mode to freeze this month's numbers permanently.`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-blue-900" data-testid="audit-pack-use-snapshot-toggle">
                  <input
                    type="checkbox"
                    checked={useSnapshotMode}
                    onChange={(e) => setUseSnapshotMode(e.target.checked)}
                    disabled={!snapshotStatus?.exists}
                    data-testid="audit-pack-use-snapshot-checkbox"
                  />
                  Snapshot mode{!snapshotStatus?.exists ? " (generate a snapshot first)" : ""}
                </label>
                <Button
                  variant="outline"
                  onClick={handleGenerateSnapshot}
                  disabled={snapshotGenerating}
                  data-testid="audit-pack-generate-snapshot-button"
                >
                  {snapshotGenerating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Generate / Refresh Snapshot
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="tab-monthly">
            <FileText className="h-4 w-4" /> Monthly Summary
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="flex items-center gap-2" data-testid="tab-suppliers">
            <Truck className="h-4 w-4" /> Supplier Balances
          </TabsTrigger>
          <TabsTrigger value="customers" className="flex items-center gap-2" data-testid="tab-customers">
            <Users className="h-4 w-4" /> Customer Balances
          </TabsTrigger>
          <TabsTrigger value="vat" className="flex items-center gap-2" data-testid="tab-vat">
            <Receipt className="h-4 w-4" /> VAT Report
          </TabsTrigger>
        </TabsList>

        {/* Monthly Summary Tab */}
        <TabsContent value="monthly" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={fetchMonthlySummary} disabled={loading} data-testid="load-monthly-btn">
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Load Summary
            </Button>
            {monthlyData && (
              <Button variant="outline" onClick={handlePrint} data-testid="print-monthly-btn">
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
            )}
          </div>
          
          {monthlyData ? (
            <Card>
              <CardContent className="pt-4">
                <div ref={printRef}>
                  <MonthlySummaryPrint data={monthlyData} companySettings={companySettings} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a period and click "Load Summary" to generate the monthly report</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Supplier Balances Tab */}
        <TabsContent value="suppliers" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={fetchSupplierBalances} disabled={loading} data-testid="load-suppliers-btn">
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Load Balances
            </Button>
            <label className="flex items-center gap-2 text-sm text-gray-700" data-testid="audit-pack-exclude-consignment-toggle">
              <input
                type="checkbox"
                checked={excludeConsignmentSuppliers}
                onChange={(e) => setExcludeConsignmentSuppliers(e.target.checked)}
                data-testid="audit-pack-exclude-consignment-checkbox"
              />
              Exclude consignment suppliers
            </label>
            {supplierData && (
              <Button variant="outline" onClick={handlePrint} data-testid="print-suppliers-btn">
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
            )}
          </div>
          
          {supplierData ? (
            <Card>
              <CardContent className="pt-4">
                <div ref={printRef}>
                  <SupplierBalancesPrint data={supplierData} companySettings={companySettings} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center text-gray-500">
                <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Click "Load Balances" to view supplier balances (accounts payable)</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Customer Balances Tab */}
        <TabsContent value="customers" className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={fetchCustomerBalances} disabled={loading} data-testid="load-customers-btn">
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Load LIVE Balances
            </Button>
            <Button
              variant="outline"
              onClick={downloadCustomerDebugJSON}
              disabled={downloadingCustomerDebug}
              data-testid="download-customer-debug-json-btn"
            >
              {downloadingCustomerDebug ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Download Debug JSON
            </Button>
            {customerData && (
              <Button variant="outline" onClick={handlePrint} data-testid="print-customers-btn">
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
            )}

            {customerData && (
              <label
                className="ml-auto flex items-center gap-2 text-sm font-medium cursor-pointer select-none px-3 py-2 border-2 rounded-md bg-amber-50 border-amber-300 text-amber-900"
                data-testid="audit-pack-debtors-only-toggle-label"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={debtorsOnly}
                  onChange={(e) => setDebtorsOnly(e.target.checked)}
                  data-testid="audit-pack-debtors-only-checkbox"
                />
                Show only customers who owe (exclude credits)
              </label>
            )}
          </div>
          
          {customerData ? (
            <Card>
              <CardContent className="pt-4">
                <div ref={printRef}>
                  <CustomerBalancesPrint data={customerData} companySettings={companySettings} debtorsOnly={debtorsOnly} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Click "Load Balances" to view customer balances (accounts receivable)</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* VAT Report Tab */}
        <TabsContent value="vat" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={fetchVATReport} disabled={loading} data-testid="load-vat-btn">
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Load VAT Report
            </Button>
            {vatData && (
              <Button variant="outline" onClick={handlePrint} data-testid="print-vat-btn">
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
            )}
          </div>
          
          {vatData ? (
            <Card>
              <CardContent className="pt-4">
                <div ref={printRef}>
                  <VATReportPrint data={vatData} companySettings={companySettings} />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center text-gray-500">
                <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a period and click "Load VAT Report" for complete VAT breakdown</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
